// @flow

/**
 * Download Image Button
 * Captures a Recharts chart as PNG and triggers download.
 * Uses recharts-to-png's useCurrentPng hook.
 */

import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Icon } from '@blueprintjs/core';
import { saveAs } from 'file-saver';

const ImageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: transparent;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

type Props = {
  title: string,
  getPng: () => Promise<string | void>,
  isLoading?: boolean,
};

const DownloadImageButton = ({ title, getPng, isLoading }: Props) => {
  const handleDownload = useCallback(async () => {
    try {
      const png = await getPng();
      if (png) {
        saveAs(png, `${title.replace(/[^a-zA-Z0-9 -]/g, '').trim()}.png`);
      }
    } catch (err) {
      console.error('Failed to capture chart image:', err);
    }
  }, [getPng, title]);

  return (
    <ImageButton
      onClick={handleDownload}
      disabled={isLoading}
      title="Download as PNG"
    >
      <Icon icon="media" iconSize={12} />
      PNG
    </ImageButton>
  );
};

export default DownloadImageButton;
